import { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ChevronRight, Award, Target, Users, Lightbulb } from 'lucide-react';

const AboutSection = () => {
  const { t, language } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      icon: Target,
      title: language === 'fr' ? 'Analyser' : 'Analyze',
      description: language === 'fr' 
        ? 'Nous analysons en profondeur votre marché, votre audience et vos concurrents pour définir la meilleure approche.'
        : 'We deeply analyze your market, audience and competitors to define the best approach.',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
    },
    {
      icon: Users,
      title: language === 'fr' ? 'Structurer' : 'Structure',
      description: language === 'fr'
        ? 'Nous construisons une architecture de contenu solide et un calendrier éditorial cohérent.'
        : 'We build a solid content architecture and a coherent editorial calendar.',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80',
    },
    {
      icon: Lightbulb,
      title: language === 'fr' ? 'Créer' : 'Create',
      description: language === 'fr'
        ? 'Nous produisons du contenu de qualité, aligné avec votre identité de marque et vos objectifs business.'
        : 'We produce quality content, aligned with your brand identity and business objectives.',
      image: 'https://images.unsplash.com/photo-1542744094-3a31f272c490?auto=format&fit=crop&w=800&q=80',
    },
    {
      icon: Award,
      title: language === 'fr' ? 'Optimiser' : 'Optimize',
      description: language === 'fr'
        ? 'Nous mesurons les performances et optimisons continuellement pour maximiser vos résultats.'
        : 'We measure performance and continuously optimize to maximize your results.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
    },
  ];

  return (
    <section 
      id="about"
      ref={ref}
      className="py-24 md:py-32 bg-white"
      data-testid="about-section"
    >
      <div className="container-custom">
        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="inline-block text-xs font-semibold tracking-[0.3em] uppercase text-neutral-500 mb-4"
          >
            {language === 'fr' ? 'Découvrez Notre Histoire' : 'Discover Our Story'}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.1 }}
            className="heading-xl text-4xl md:text-5xl lg:text-6xl mb-6 text-black"
            data-testid="about-title"
          >
            {t('about.title')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="text-lg text-neutral-600 leading-relaxed"
            data-testid="about-description"
          >
            {t('about.description')}
          </motion.p>
        </div>

        {/* Interactive Feature Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: Feature Tabs */}
          <div className="space-y-4">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              const isActive = activeFeature === index;
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.1, type: "spring", stiffness: 100 }}
                  onClick={() => setActiveFeature(index)}
                  className={`relative p-6 rounded-2xl cursor-pointer transition-all duration-300 border ${
                    isActive 
                      ? 'bg-black text-white border-black shadow-xl' 
                      : 'bg-white hover:bg-neutral-50 border-neutral-200'
                  }`}
                  whileHover={{ x: 8, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`feature-tab-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-white/20' : 'bg-neutral-100'
                    }`}>
                      <IconComponent className={`w-6 h-6 ${isActive ? 'text-white' : 'text-black'}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-serif text-lg font-semibold mb-2 ${
                        isActive ? 'text-white' : 'text-black'
                      }`}>
                        {feature.title}
                      </h3>
                      <p className={`text-sm ${isActive ? 'text-white/80' : 'text-neutral-500'}`}>
                        {feature.description}
                      </p>
                    </div>
                    <ChevronRight className={`w-5 h-5 flex-shrink-0 transition-transform ${
                      isActive ? 'text-white rotate-90' : 'text-neutral-400'
                    }`} />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Right: Dynamic Image */}
          <div className="relative h-[400px] md:h-[500px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeFeature}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl"
              >
                <img
                  src={features[activeFeature].image}
                  alt={features[activeFeature].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg">
                    <h4 className="text-black font-semibold text-lg">
                      {features[activeFeature].title}
                    </h4>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Floating Badge */}
            <motion.div
              className="absolute -top-4 -right-4 w-20 h-20 bg-black rounded-2xl flex items-center justify-center shadow-xl z-10"
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
            >
              <span className="text-white font-serif text-xl font-bold">10+</span>
            </motion.div>
            <motion.div
              className="absolute -bottom-4 -left-4 bg-white px-4 py-2 rounded-full shadow-xl text-sm font-medium border border-neutral-200 z-10"
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              {language === 'fr' ? '✨ Années d\'expérience' : '✨ Years of experience'}
            </motion.div>
          </div>
        </div>

        {/* CTA Button */}
        <motion.div 
          className="mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
        >
          <motion.a
            href="#services"
            whileHover={{ 
              scale: 1.05, 
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              y: -3
            }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-3 btn-pill btn-primary relative overflow-hidden group"
            data-testid="about-cta"
          >
            <motion.span
              className="absolute inset-0 bg-gradient-to-r from-neutral-800 to-neutral-700"
              initial={{ x: '-100%' }}
              whileHover={{ x: '0%' }}
              transition={{ duration: 0.4 }}
            />
            <span className="relative z-10">{t('about.cta')}</span>
            <motion.div
              className="relative z-10"
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ChevronRight className="w-5 h-5" />
            </motion.div>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
